import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import { EodSummaryBrief } from '../../api/eodApi';
import DayOfWeekHeatmap from './DayOfWeekHeatmap';
import {
  chartColors,
  gridProps,
  axisTick,
  quietAxisProps,
  tooltipContentStyle as sharedTooltipContentStyle,
  barRadius,
  MAX_BAR_SIZE,
  SERIES_FILL_OPACITY,
  activeBarProps,
  legendProps,
} from './chartTheme';

interface Props {
  summaries: EodSummaryBrief[];
  onDayClick?: (date: string) => void;
}

function dayLabel(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

const EodHistoryCharts: React.FC<Props> = ({ summaries, onDayClick }) => {
  const { theme } = useTheme();

  const cardSx = {
    backgroundColor: theme.components?.card?.background || '#FFF',
    border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
    borderRadius: '12px',
    p: { xs: 1.5, sm: 2 },
  };
  const sectionLabel = {
    color: theme.gray?.[500] || '#666',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    fontSize: '0.65rem',
    letterSpacing: '0.05em',
  };
  const tickStyle = axisTick;
  const tooltipContentStyle = sharedTooltipContentStyle;

  // --- Card 1 trend data ---
  const trendData = useMemo(() => {
    return [...summaries]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((s) => {
        const gross = Number(s.grossRevenue || 0);
        const net = Number(s.netRevenue || 0);
        const margin = gross > 0 ? Number(((net / gross) * 100).toFixed(1)) : 0;
        return {
          date: s.date,
          label: dayLabel(s.date),
          gross,
          net,
          margin,
          status: s.status,
        };
      });
  }, [summaries]);

  // --- Card 2 heatmap data ---
  const heatmapData = useMemo(
    () =>
      summaries.map((s) => ({
        date: s.date,
        income: Number(s.grossRevenue || 0),
        expenses: Number(s.totalExpenses || 0),
      })),
    [summaries],
  );

  // --- Card 3 drift / variance ---
  const { totalVariance, draftCount, worstVariance } = useMemo(() => {
    let varSum = 0;
    let drafts = 0;
    summaries.forEach((s) => {
      varSum += Number(s.cashVariance || 0);
      if (s.status !== 'CONFIRMED') drafts += 1;
    });
    const worst = [...summaries]
      .filter((s) => Math.abs(Number(s.cashVariance || 0)) > 0)
      .sort((a, b) => Math.abs(Number(b.cashVariance || 0)) - Math.abs(Number(a.cashVariance || 0)))
      .slice(0, 5);
    return { totalVariance: varSum, draftCount: drafts, worstVariance: worst };
  }, [summaries]);

  if (summaries.length === 0) return null;

  const barColor = (status: string) => (status === 'CONFIRMED' ? chartColors.income : theme.gray?.[300] || gridProps.stroke);

  const trendHeight = { xs: 220, sm: 260 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
      {/* Card 1 — Revenue trend + margin */}
      <Box sx={cardSx}>
        <Typography variant="caption" sx={sectionLabel}>
          Revenue trend · bars = gross, line = net margin %
        </Typography>
        <Box sx={{ height: trendHeight, mt: 0.5, mx: { xs: -0.5, sm: 0 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={trendData} margin={{ top: 12, right: 12, left: 0, bottom: 8 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="label" tick={tickStyle} interval="preserveStartEnd" {...quietAxisProps} />
              <YAxis
                yAxisId="left"
                tick={tickStyle}
                tickFormatter={(v: number) => formatCurrency(v, { showDecimals: false, compact: true })}
                {...quietAxisProps}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={tickStyle}
                tickFormatter={(v: number) => `${v}%`}
                domain={[0, 100]}
                {...quietAxisProps}
              />
              <Tooltip
                formatter={(value: number, name: string) =>
                  name === 'Margin %' ? [`${value}%`, name] : [formatCurrency(value, { showDecimals: false }), name]
                }
                labelFormatter={(_l: string, payload: ReadonlyArray<{ payload?: { date?: string } }>) => {
                  const p = payload?.[0]?.payload;
                  return p?.date ? new Date(p.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : '';
                }}
                contentStyle={tooltipContentStyle}
              />
              <Legend {...legendProps} />
              <Bar
                yAxisId="left"
                dataKey="gross"
                name="Gross"
                radius={barRadius}
                fillOpacity={SERIES_FILL_OPACITY}
                maxBarSize={MAX_BAR_SIZE}
                cursor={onDayClick ? 'pointer' : 'default'}
                onClick={(p: { date?: string }) => p?.date && onDayClick?.(p.date)}
                {...activeBarProps}
              >
                {trendData.map((d) => (
                  <Cell key={d.date} fill={barColor(d.status)} />
                ))}
              </Bar>
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="margin"
                name="Margin %"
                stroke={theme.warning || '#C9A961'}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
          <LegendDot color={chartColors.income} label="Confirmed" />
          <LegendDot color={theme.gray?.[300] || '#CCC'} label="Draft" />
        </Box>
      </Box>

      {/* Card 2 + Card 3 side by side on md+ */}
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr' } }}>
        {/* Card 2 — DoW heatmap */}
        <Box sx={cardSx}>
          <Typography variant="caption" sx={sectionLabel}>
            Day-of-week revenue pattern
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <DayOfWeekHeatmap data={heatmapData} metric="income" />
          </Box>
        </Box>

        {/* Card 3 — Variance watchlist */}
        <Box sx={cardSx}>
          <Typography variant="caption" sx={sectionLabel}>
            Cash variance · drift watchlist
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1.5,
              mt: 1,
            }}
          >
            <Kpi
              label="Total variance"
              value={formatCurrency(totalVariance, { showDecimals: false })}
              tone={Math.abs(totalVariance) > 0 ? (totalVariance < 0 ? theme.danger : theme.warning) : theme.text}
            />
            <Kpi
              label="Drafts open"
              value={String(draftCount)}
              tone={draftCount > 0 ? theme.warning : theme.text}
            />
          </Box>

          {worstVariance.length > 0 ? (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${theme.gray?.[200] || '#E5E5E5'}` }}>
              <Typography variant="caption" sx={sectionLabel}>
                Worst {worstVariance.length} variance days
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                {worstVariance.map((s) => {
                  const v = Number(s.cashVariance || 0);
                  return (
                    <Box
                      key={s.date}
                      onClick={() => onDayClick?.(s.date)}
                      role={onDayClick ? 'button' : undefined}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1,
                        borderRadius: 1,
                        cursor: onDayClick ? 'pointer' : 'default',
                        backgroundColor: theme.gray?.[100] || '#F5F5F5',
                        '&:hover': onDayClick ? { backgroundColor: `${theme.primary}18` } : undefined,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: theme.text }}>
                        {new Date(s.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: v < 0 ? theme.danger : theme.warning }}>
                        {formatCurrency(v, { showDecimals: false })}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : (
            <Typography sx={{ mt: 1.5, fontSize: '0.8rem', color: theme.gray?.[400], textAlign: 'center' }}>
              No cash variance in range
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

interface KpiProps {
  label: string;
  value: string;
  tone?: string;
}
const Kpi: React.FC<KpiProps> = ({ label, value, tone }) => {
  const { theme } = useTheme();
  return (
    <Box sx={{ p: 1, borderRadius: 1, backgroundColor: theme.gray?.[100] || '#F5F5F5' }}>
      <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', color: theme.gray?.[500], letterSpacing: '0.05em', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: tone || theme.text }}>{value}</Typography>
    </Box>
  );
};

interface LegendProps {
  color: string;
  label: string;
}
const LegendDot: React.FC<LegendProps> = ({ color, label }) => {
  const { theme } = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: color }} />
      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: theme.gray?.[500] }}>
        {label}
      </Typography>
    </Box>
  );
};

export default EodHistoryCharts;
